import { DirectionalHint } from '@fluentui/react';
import {
    IDatasetTypeMetadata,
    IDynamicDatasetTypeMetadata
} from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { dynamicRangePartitionControls, getTableActions, partitionOptionConst, partitionOptionForwardLinkMap, queryTimeoutMetadata } from '../consts/metadata-consts';
import { ColumnPhysicalType, ColumnType, defaultSupportedColumnTypes } from 'app/shared/utils/column-type.util';
import { GlobalVariables } from 'app/services/globals';
import { compact } from 'lodash';
import {
    getAppendOption,
    getOverwriteOption,
    getSCD2Option,
    getUpsertOption
} from 'app/registries/consts/copy-job-const';
import { format } from 'app/shared/utils/string.util';
import { Column } from 'shell-models-lib';

export const lakehouseTableMetadata: IDynamicDatasetTypeMetadata = async (injector) => {
    const metadata: IDatasetTypeMetadata = {
        type: adfModelTypeMap.lakehouseTable.datasetType,
        displayName: stringResources.LakehouseTable_DisplayText,
        supportLinkedServiceTypes: [adfModelTypeMap.lakehouse.linkedServiceType],
        isSource: true,
        isSink: true,
        datasetKind: 'table',
        icon: {
            className: 'svg-i_dataLakehouse'
        },
        supportAutoTableCreation: true,
        isFirstClass: true,
        notSupportCopySourceQuery: !__IS_FABRIC_DPE__,
        queryDisplayName: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.TSQLQueryLabel),
        queryDisplayNameInPlural: format(stringResources.ModelName_DisplayTextPreviewTemplate, stringResources.TSQLQueriesLabel),
        queryAlias: 'sqlReaderQuery',
        supportCreatedAsV2: true,
        isTableRequired: true,
        supportDeleteActivity: false, // temporary
        supportGetMetadataActivity: true,
        supportDataTypes: defaultSupportedColumnTypes.concat([ColumnType.SByte, ColumnType.Date]),
        supportEditPhysicalType: true,
        supportDataFlowSink: true,
        supportDataFlowSource: true,
        splitTablePath: true,
        category: __IS_FABRIC_DPE__ ? ['Workspace'] : ['Database'],
        copySourceControls: [
            {
                type: 'parameterizable',
                hide: `{mode !== 'table'}`,
                child: {
                    type: 'date-time-picker',
                    bind: 'timestampAsOf',
                    placeholder: 'YYYY-MM-DDThh:mm:ss',
                    description: stringResources.timestampAsOfDesc,
                    label: stringResources.DataFlowColumnType_Timestamp,
                    drops: DirectionalHint.topLeftEdge,
                    styleOverrides: {
                        width: '496px' // label 215 + input 264 + info 17
                    }
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                type: 'parameterizable',
                hide: `{mode !== 'table'}`,
                child: {
                    type: 'textbox-number',
                    bind: 'versionAsOf',
                    description: stringResources.versionAsOfDesc,
                    label: stringResources.VersionLabel,
                    placeholder: '0, 1, 2...',
                    inputType: 'number',
                    min: 0
                },
                dynamicContentTextClass: 'input-spaced'
            },
            {
                ...queryTimeoutMetadata,
                hide: `{mode !== 'query'}`
            },
            {
                type: 'radio-switch',
                hide: `{mode !== 'query'}`,
                label: stringResources.ModelName_Partition_Option,
                defaultValue: partitionOptionConst.none,
                description:
                    '<a target="_blank" href="' +
                    partitionOptionForwardLinkMap[adfModelTypeMap.lakehouseTable.copySourceType] +
                    '">' +
                    stringResources.LearnMore +
                    '</a>',
                escapeDescription: false,
                descriptionLink: partitionOptionForwardLinkMap[adfModelTypeMap.lakehouseTable.copySourceType],
                options: [
                    {
                        label: stringResources.None,
                        value: partitionOptionConst.none
                    },
                    {
                        label: stringResources.PartitionOptionType_DynamicRange,
                        value: partitionOptionConst.dyamicRange,
                        description: format(
                            stringResources.Partition_DynamicRange_Tooltip_Template,
                            '(?DfDynamicRangePartitionCondition)',
                            'SELECT * FROM <TableName> WHERE ?DfDynamicRangePartitionCondition'
                        )
                    }
                ],
                bind: 'partitionOption',
                isParameterizable: true,
                inlineHintMessage: stringResources.Sql_Partition_Info,
                cases: [
                    {
                        caseValue: partitionOptionConst.dyamicRange,
                        children: [...dynamicRangePartitionControls]
                    }
                ]
            }
        ],
        copySinkControls: compact([
            __IS_FABRIC_DPE__ && GlobalVariables.timestampNtz
                ? {
                    type: 'parameterizable',
                    child: {
                        type: 'checkbox',
                        bind: 'enableTimestampNtz',
                        label: stringResources.Dataset_enableTimestampNtz,
                        description: stringResources.Dataset_enableTimestampNtz_Description,
                        escapeDescription: false,
                        labelPosition: 'left'
                    },
                    dynamicContentTextClass: 'input-spaced'
                }
                : undefined,
            __IS_FABRIC_DPE__
                ? {
                    type: 'parameterizable',
                    child: {
                        type: 'checkbox',
                        bind: 'applyVOrder',
                        label: stringResources.Dataset_applyVOrder,
                        description: stringResources.Dataset_applyVOrder_Description,
                        escapeDescription: false,
                        labelPosition: 'left',
                        defaultValue: true,
                        useDefaultValueWhenValueIsNull: true
                    },
                    dynamicContentTextClass: 'input-spaced'
                }
                : undefined,
            ...getTableActions(__IS_FABRIC_DPE__)
        ]),
        LogicalToPhysicalMapping: (sourceColumn: Column, options?: { enableTimestampNtz?: boolean }) => {
            // used when generate sink from source, need to map logicalType to physicalType
            return {
                // eslint-disable-next-line id-blacklist
                boolean: 'boolean',
                sbyte: 'byte',
                byte: 'short',
                'byte[]': 'binary',
                datetime: !GlobalVariables.timestampNtz || !options?.enableTimestampNtz || !__IS_FABRIC_DPE__  || sourceColumn?.datetimeKind?.toLowerCase() === 'utc'  ? 'timestamp' : 'timestamp_ntz',
                datetimeoffset: 'timestamp',
                decimal: 'decimal',
                double: 'double',
                single: 'float',
                // eslint-disable-next-line id-blacklist
                string: 'string',
                guid: 'string',
                int16: 'short',
                int32: 'integer',
                int64: 'long',
                uint16: 'integer',
                uint32: 'long',
                uint64: 'decimal',
                date: 'date'
            }
        },
        PhysicalToLogicalMapping: {
            // used when editing physical type to handle related checks
            binary: ColumnType.ByteArray,
            // eslint-disable-next-line id-blacklist
            boolean: ColumnType.Boolean,
            byte: ColumnType.SByte,
            date: ColumnType.Date,
            decimal: ColumnType.Decimal,
            double: ColumnType.Double,
            float: ColumnType.Single,
            integer: ColumnType.Int32,
            long: ColumnType.Int64,
            short: ColumnType.Int16,
            // eslint-disable-next-line id-blacklist
            string: ColumnType.String,
            timestamp: ColumnType.DateTime,
            timestamp_ntz: ColumnType.DateTime
        },
        supportedPhysicalTypes: compact([
            ColumnPhysicalType.Boolean,
            ColumnPhysicalType.Byte,
            ColumnPhysicalType.Short,
            ColumnPhysicalType.Binary,
            ColumnPhysicalType.Date,
            ColumnPhysicalType.Timestamp,
            ColumnPhysicalType.Decimal,
            ColumnPhysicalType.Double,
            ColumnPhysicalType.Float,
            ColumnPhysicalType.String,
            ColumnPhysicalType.Integer,
            ColumnPhysicalType.Long,
            GlobalVariables.timestampNtz && __IS_FABRIC_DPE__ ? ColumnPhysicalType.Timestamp_ntz : undefined
        ]),
        conversionNotSupportedPhysicalTypes: {
            Parquet: ['time_millis', 'time_micros', 'time_nanos']
        },
        precisionMap: {
            decimal: {
                minValue: 1,
                maxValue: 38
            }
        },
        scaleMap: {
            decimal: {
                minValue: 0,
                maxValue: 38,
                defaultValue: 18,
                smallerThanPrecision: true
            }
        },
        copyJob: {
            updateMethod: {
                supportUpdateMethod: true,
                options: [getAppendOption(), getOverwriteOption(), getUpsertOption(), getSCD2Option()],
                updateMethodProperties: ['tableActionOption', 'upsertSettings'],
                defaultUpdateMethod: 'Overwrite',
                upsertKeyColumnRequired: true
            },
            fullLoadBehavior: {
                supportFullLoadBehavior: true
            },
            multipleQuery: true,
            sourcePartition: {
                supportSourcePartition: true
            },
            sinkControls: compact([
                {
                    type: 'checkbox',
                    bind: 'applyVOrder',
                    label: stringResources.Dataset_applyVOrder,
                    description: stringResources.Dataset_applyVOrder_Description,
                    escapeDescription: false,
                    defaultValue: true,
                    useDefaultValueWhenValueIsNull: true
                },
                GlobalVariables.timestampNtz
                    ? {
                        type: 'checkbox',
                        bind: 'enableTimestampNtz',
                        hide: '{ !isTimestampNtzSupported }',
                        label: stringResources.Dataset_enableTimestampNtz,
                        description: stringResources.Dataset_enableTimestampNtz_Description,
                        escapeDescription: false
                    }
                    : undefined,
                GlobalVariables.cdfAutoCreate
                    ? {
                        type: 'checkbox',
                        bind: 'enableChangeDataFeed',
                        label: stringResources.CopyWizard_Summary_EnableCDFCopy,
                        description: stringResources.CopyWizard_Summary_EnableCDFCopy_Desc
                    }
                    : undefined,
                {
                    type: 'textbox-number',
                    bind: 'maxRowsPerFile',
                    label: stringResources.ModelName_MaxRowsPerFileLabel,
                    inputType: 'number',
                    min: 1
                }
            ])
        }
    };

    return metadata;
};
